import { useState, useEffect, useCallback } from "react";

interface GalleryViewerProps {
    photos: string[]
}

export const GalleryViewer = ({ photos }: GalleryViewerProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [current, setCurrent] = useState(0);
    // Nowy stan do kontrolowania animacji powiększenia
    const [animateIn, setAnimateIn] = useState(false);


    const images = photos;

    const open = (idx: number) => {
        // Zresetuj stan animacji przed otwarciem modalu
        setAnimateIn(false);
        setCurrent(idx);
        setIsOpen(true);
        // Opcjonalnie: zablokuj przewijanie ciała strony
        document.body.style.overflow = 'hidden';
    };

    const close = () => {
        // Gdy zamykamy, natychmiast ustawiamy animateIn na false (choć CSS transition handling might make this less critical depending on how exit animation is handled, but good practice)
        setAnimateIn(false);
        setIsOpen(false);
        // Opcjonalnie: przywróć przewijanie ciała strony
        document.body.style.overflow = 'auto';
    };

    // Funkcje next/prev
    const next = useCallback(() => setCurrent((c) => (c + 1) % images.length), [images.length]);
    const prev = useCallback(() => setCurrent((c) => (c - 1 + images.length) % images.length), [images.length]);

    // Efekt do uruchomienia animacji po otwarciu modalu
    useEffect(() => {
        if (isOpen) {
            // Użyjemy setTimeout z małym opóźnieniem, aby przeglądarka zdążyła wyrenderować
            // początkowy stan przed zastosowaniem końcowych stylów i uruchomieniem przejścia.
            const timer = setTimeout(() => {
                setAnimateIn(true);
            }, 10); // Małe opóźnienie np. 10ms
            return () => clearTimeout(timer); // Czyszczenie timera
        }
    }, [isOpen]); // Ten efekt uruchamia się, gdy zmienia się stan isOpen

    // Dodanie obsługi klawiszy strzałek
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isOpen) return;
            
            if (e.key === 'ArrowRight') {
                next();
            } else if (e.key === 'ArrowLeft') {
                prev();
            } else if (e.key === 'Escape') {
                close();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        
        // Sprzątanie przy odmontowaniu komponentu
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, next, prev, close]);

    return (
        <div>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", justifyContent: "center" }}>
                {images.map((src, idx) => (
                    <img
                        key={idx}
                        src={src}
                        alt={`Zdjęcie ${idx + 1}`}
                        style={{ height: 120, cursor: "pointer", borderRadius: 6, boxShadow: "0 1px 4px #aaa" }}
                        onClick={() => open(idx)}
                    />
                ))}
            </div>
            {isOpen && (
                <div style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    backgroundColor: "rgba(0,0,0,0.6)", // Bardziej przezroczyste tło
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1000,
                    cursor: 'pointer', // Kursor wskazujący na klikalność tła
                }}
                onClick={close} // Kliknięcie na tło zamyka modal
                >
                    {/* Kontener obrazu w modalu */}
                    <div
                        style={{
                            position: "relative",
                            zIndex: 1050,
                            // Usunięto onClick z tego diva
                        }}
                    >
                        {/* Przycisk X do zamknięcia */}
                        <div
                            style={{
                                position: 'absolute',
                                top: '-40px',
                                right: '-40px',
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                zIndex: 1051,
                                transition: 'background-color 0.2s ease'
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                close();
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.7)';
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M18 6L6 18" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M6 6L18 18" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>

                        {/* Strzałka w lewo */}
                        <div
                            style={{
                                position: 'absolute',
                                left: '-60px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                zIndex: 1051,
                                transition: 'background-color 0.2s ease'
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                prev();
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.7)';
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15 18L9 12L15 6" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>

                        {/* Strzałka w prawo */}
                        <div
                            style={{
                                position: 'absolute',
                                right: '-60px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                zIndex: 1051,
                                transition: 'background-color 0.2s ease'
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                next();
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.7)';
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9 6L15 12L9 18" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>

                         <img
                            src={images[current]}
                            alt="Powiększone"
                            style={{
                                maxHeight: "80vh",
                                maxWidth: "90vw",
                                borderRadius: 8,
                                display: 'block',
                                // DODANO STYLE ANIMACJI
                                // Początkowe style (przed animateIn=true)
                                opacity: animateIn ? 1 : 0, // Jeśli animateIn=true, opacity=1, w przeciwnym razie 0
                                transform: animateIn ? 'scale(1)' : 'scale(0.8)', // Jeśli animateIn=true, scale=1, w przeciwnym razie 0.8 (można dostosować wartość początkową np. 0.5)
                                // Właściwość transition do animowania zmian opacity i transform
                                transition: 'opacity 0.3s ease-out, transform 0.3s ease-out', // Czas trwania i funkcja przejścia
                            }}
                            onClick={e => e.stopPropagation()} // Ważne: zatrzymaj propagację kliknięcia na samym obrazie
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
